import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Crown, History, UserMinus, UserPlus, Users } from 'lucide-react';
import { ADMISSION_SUPPORT_ROLES, type AdmissionCareTeamDto, type AdmissionCareTeamMemberDto, type AdmissionSupportRole } from '@his/shared';
import { tr } from '@/lib/i18n-core';
import { del, post, put } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { dateTime } from '@/lib/utils';
import { ConfirmDialog, Dialog } from '@/components/ui/layout';
import { Alert, Badge, Button, Checkbox, Field, Select, Spinner } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { ShellEmpty, ShellPanel, ShellSectionHeading } from '@/components/clinical-shell/ShellPanel';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { careRoleLabel } from '../AdmissionPatientBar';
import { admissionKey, careTeamKey, useCareTeamCandidates } from '../queries';
import type { Admission } from '../types';

function MemberRow({ member, onRemove }: { member: AdmissionCareTeamMemberDto; onRemove?: () => void }) {
  const primary = member.role === 'primary_consultant';
  return (
    <li className="flex flex-wrap items-center gap-3 rounded-xl border px-3 py-2.5">
      <span className={primary ? 'grid h-8 w-8 place-items-center rounded-lg bg-primary text-primary-foreground' : 'grid h-8 w-8 place-items-center rounded-lg bg-muted text-muted-foreground'} aria-hidden>
        {primary ? <Crown className="h-4 w-4" /> : <Users className="h-4 w-4" />}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{member.name ?? tr('ipd', 'ws_unknown_doctor')}</p>
        <p className="text-xs text-muted-foreground">
          {[member.designation, member.addedBy ? tr('ipd', 'ws_added_by_x_on_x', { name: member.addedBy, date: dateTime(member.addedAt) }) : null].filter(Boolean).join(' · ')}
        </p>
      </div>
      <Badge variant={primary ? 'default' : 'secondary'}>{careRoleLabel(member.role)}</Badge>
      {onRemove && (
        <Button size="sm" variant="ghost" onClick={onRemove}>
          <UserMinus className="h-4 w-4" aria-hidden /> {tr('ipd', 'ws_remove')}
        </Button>
      )}
    </li>
  );
}

export function CareTeamSection({ admission, team, loading }: { admission: Admission; team: AdmissionCareTeamDto | undefined; loading: boolean }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [adding, setAdding] = useState(false);
  const [handingOver, setHandingOver] = useState(false);
  const [removing, setRemoving] = useState<AdmissionCareTeamMemberDto | null>(null);
  const mayEdit = Boolean(team?.mayEdit);
  const refresh = (next?: AdmissionCareTeamDto) => {
    if (next) queryClient.setQueryData(careTeamKey(admission.id), next);
    void queryClient.invalidateQueries({ queryKey: admissionKey(admission.id) });
    void queryClient.invalidateQueries({ queryKey: careTeamKey(admission.id) });
  };
  const remove = useMutation({
    mutationFn: (member: AdmissionCareTeamMemberDto) => del<AdmissionCareTeamDto>(`/ipd/admissions/${admission.id}/care-team/${member.id}`),
    onSuccess: (next) => {
      toast.success(tr('ipd', 'ws_member_removed'));
      refresh(next);
    },
    onError: (error) => toast.error(tr('ipd', 'ws_could_not_change_the_care_team'), describeError(error)),
  });

  return (
    <>
      <ShellSectionHeading
        icon={Users}
        title={tr('ipd', 'ws_care_team')}
        description={tr('ipd', 'ws_care_team_desc')}
        actions={
          mayEdit ? (
            <>
              <Button size="sm" variant="outline" onClick={() => setHandingOver(true)}>
                <Crown className="h-4 w-4" aria-hidden /> {tr('ipd', 'ws_change_primary')}
              </Button>
              <Button size="sm" onClick={() => setAdding(true)}>
                <UserPlus className="h-4 w-4" aria-hidden /> {tr('ipd', 'ws_add_doctor')}
              </Button>
            </>
          ) : null
        }
      />
      <ShellPanel icon={Users} title={tr('ipd', 'ws_doctors_on_this_admission')} description={tr('ipd', 'ws_nurses_come_from_the_ward_roster')}>
        {loading && <Spinner />}
        {!loading && (team?.members.length ?? 0) === 0 && <ShellEmpty>{tr('ipd', 'ws_no_care_team')}</ShellEmpty>}
        <ul className="space-y-2">
          {team?.members.map((member) => (
            <MemberRow key={member.id} member={member} onRemove={mayEdit && member.role !== 'primary_consultant' ? () => setRemoving(member) : undefined} />
          ))}
        </ul>
      </ShellPanel>
      {(team?.past.length ?? 0) > 0 && (
        <ShellPanel icon={History} title={tr('ipd', 'ws_past_members')}>
          <ul className="space-y-1.5 text-sm">
            {team?.past.map((member) => (
              <li key={member.id} className="flex flex-wrap items-center gap-2 text-muted-foreground">
                <span className="font-medium text-foreground/80">{member.name ?? tr('ipd', 'ws_unknown_doctor')}</span>
                <Badge variant="muted">{careRoleLabel(member.role)}</Badge>
                <span className="text-xs">
                  {tr('ipd', 'ws_served_from_x_to_x', { from: dateTime(member.addedAt), to: dateTime(member.removedAt) })}
                  {member.removedBy ? ` · ${tr('ipd', 'ws_by_x', { name: member.removedBy })}` : ''}
                </span>
              </li>
            ))}
          </ul>
        </ShellPanel>
      )}
      {adding && (
        <AddMemberDialog
          admission={admission}
          team={team}
          onClose={() => setAdding(false)}
          onSaved={(next) => {
            setAdding(false);
            refresh(next);
          }}
        />
      )}
      {handingOver && (
        <ChangePrimaryDialog
          admission={admission}
          team={team}
          onClose={() => setHandingOver(false)}
          onSaved={(next) => {
            setHandingOver(false);
            refresh(next);
          }}
        />
      )}
      {removing && (
        <ConfirmDialog
          title={tr('ipd', 'ws_remove_from_care_team')}
          description={tr('ipd', 'ws_remove_from_care_team_desc', { name: removing.name ?? '' })}
          destructive
          confirmLabel={tr('ipd', 'ws_remove')}
          onConfirm={() => remove.mutateAsync(removing)}
          onClose={() => setRemoving(null)}
        />
      )}
    </>
  );
}

function AddMemberDialog({
  admission,
  team,
  onClose,
  onSaved,
}: {
  admission: Admission;
  team: AdmissionCareTeamDto | undefined;
  onClose: () => void;
  onSaved: (team: AdmissionCareTeamDto) => void;
}) {
  const [userId, setUserId] = useState('');
  const [role, setRole] = useState<AdmissionSupportRole>('co_consultant');
  const candidates = useCareTeamCandidates(admission.branchId, true);
  const onTeam = new Set((team?.members ?? []).filter((member) => member.role === 'primary_consultant').map((member) => member.userId));
  const options = (candidates.data ?? []).filter((doctor) => !onTeam.has(doctor.id));
  const add = useMutation({
    mutationFn: () => post<AdmissionCareTeamDto>(`/ipd/admissions/${admission.id}/care-team`, { userId, role }),
    onSuccess: onSaved,
  });
  return (
    <Dialog
      open
      onClose={onClose}
      icon={UserPlus}
      title={tr('ipd', 'ws_add_doctor')}
      description={tr('ipd', 'ws_add_doctor_desc')}
      dirty={userId !== ''}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {tr('ipd', 'cancel')}
          </Button>
          <Button onClick={() => add.mutate()} loading={add.isPending} disabled={!userId}>
            {tr('ipd', 'ws_add')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label={tr('ipd', 'ws_doctor')} required>
          <Select value={userId} onChange={(e) => setUserId(e.target.value)}>
            <option value="">{candidates.isLoading ? tr('ipd', 'loading') : tr('ipd', 'ws_choose_a_doctor')}</option>
            {options.map((doctor) => (
              <option key={doctor.id} value={doctor.id}>
                {doctor.designation ? `${doctor.name} · ${doctor.designation}` : doctor.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={tr('ipd', 'ws_role')} required>
          <ChoiceButtons value={role} onChange={setRole} label={tr('ipd', 'ws_role')} options={ADMISSION_SUPPORT_ROLES.map((value) => ({ value, label: careRoleLabel(value) }))} />
        </Field>
        {add.error && <Alert variant="destructive">{describeError(add.error)}</Alert>}
      </div>
    </Dialog>
  );
}

function ChangePrimaryDialog({
  admission,
  team,
  onClose,
  onSaved,
}: {
  admission: Admission;
  team: AdmissionCareTeamDto | undefined;
  onClose: () => void;
  onSaved: (team: AdmissionCareTeamDto) => void;
}) {
  const [userId, setUserId] = useState('');
  const [keepPrevious, setKeepPrevious] = useState(true);
  const candidates = useCareTeamCandidates(admission.branchId, true);
  const options = (candidates.data ?? []).filter((doctor) => doctor.id !== team?.primaryUserId);
  const previous = team?.members.find((member) => member.role === 'primary_consultant');
  const change = useMutation({
    mutationFn: () => put<AdmissionCareTeamDto>(`/ipd/admissions/${admission.id}/care-team/primary`, { userId, keepPrevious }),
    onSuccess: onSaved,
  });
  return (
    <Dialog
      open
      onClose={onClose}
      icon={Crown}
      title={tr('ipd', 'ws_change_primary')}
      description={tr('ipd', 'ws_change_primary_desc')}
      dirty={userId !== ''}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {tr('ipd', 'cancel')}
          </Button>
          <Button onClick={() => change.mutate()} loading={change.isPending} disabled={!userId}>
            {tr('ipd', 'ws_hand_over')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label={tr('ipd', 'ws_new_primary_consultant')} required>
          <Select value={userId} onChange={(e) => setUserId(e.target.value)}>
            <option value="">{candidates.isLoading ? tr('ipd', 'loading') : tr('ipd', 'ws_choose_a_doctor')}</option>
            {options.map((doctor) => (
              <option key={doctor.id} value={doctor.id}>
                {doctor.designation ? `${doctor.name} · ${doctor.designation}` : doctor.name}
              </option>
            ))}
          </Select>
        </Field>
        {previous && (
          <Checkbox
            checked={keepPrevious}
            onCheckedChange={setKeepPrevious}
            label={tr('ipd', 'ws_keep_x_as_co_consultant', { name: previous.name ?? tr('ipd', 'ws_unknown_doctor') })}
          />
        )}
        {change.error && <Alert variant="destructive">{describeError(change.error)}</Alert>}
      </div>
    </Dialog>
  );
}
